import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Stack, TextField, Button, Typography, Alert, CircularProgress, Box, Link, LinearProgress } from '@mui/material';
import OnboardingLayout from 'layout/OnboardingLayout';
import AuthCardWrapper from '../AuthCardWrapper';
import LogoBg from 'assets/images/logo_bg.png';
import authService from 'api/authService';

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');
    const [token, setToken] = useState('');
    const [timer, setTimer] = useState(0);
    const inputRefs = useRef([]);

    // Timer Logic for Resend
    useEffect(() => {
        let interval;
        if (timer > 0) interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
        return () => clearInterval(interval);
    }, [timer]);

    // Password Strength Logic
    const getStrength = (pw) => {
        let s = 0;
        if (pw.length >= 8) s += 25;
        if (/[A-Z]/.test(pw)) s += 25;
        if (/[0-9]/.test(pw)) s += 25;
        if (/[^A-Za-z0-9]/.test(pw)) s += 25;
        return s;
    };

    const getStrengthColor = (s) => {
        if (s <= 25) return 'error';
        if (s <= 50) return 'warning';
        if (s <= 75) return 'info';
        return 'success';
    };

    const formik = useFormik({
        initialValues: {
            email: '',
            otp: Array(4).fill(''),
            password: '',
            confirmPassword: ''
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Invalid email').required('Required'),
            password: step === 2 ? Yup.string().min(8, 'Minimum 8 characters').required('Required') : Yup.string(),
            confirmPassword:
                step === 2
                    ? Yup.string()
                          .oneOf([Yup.ref('password'), null], 'Passwords must match')
                          .required('Required')
                    : Yup.string()
        }),
        onSubmit: async (values) => {
            setServerError('');
            setLoading(true);
            try {
                if (step === 0) {
                    const res = await authService.forgotPassword(values.email);
                    setToken(res.token);
                    setStep(1);
                    setTimer(60);
                } else if (step === 1) {
                    const res = await authService.verifyForgotPasswordOtp(token, values.otp.join(''));
                    setToken(res.token);
                    setStep(2);
                } else if (step === 2) {
                    await authService.resetPassword(token, values.password);
                    setStep(3);
                }
            } catch (err) {
                setServerError(err?.response?.data?.message || 'Action failed');
            } finally {
                setLoading(false);
            }
        }
    });

    const handleResend = async () => {
        if (timer > 0) return;
        setLoading(true);
        try {
            const res = await authService.forgotPassword(formik.values.email);
            setToken(res.token);
            setTimer(60);
        } catch (err) {
            setServerError('Failed to resend code');
        } finally {
            setLoading(false);
        }
    };

    return (
        <OnboardingLayout title="Reset Password" subtitle={step < 3 ? `Step ${step + 1} of 3` : ''} imageSrc={LogoBg}>
            <AuthCardWrapper>
                {serverError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {serverError}
                    </Alert>
                )}

                <form onSubmit={formik.handleSubmit}>
                    {step === 0 && (
                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                error={formik.touched.email && !!formik.errors.email}
                                helperText={formik.touched.email && formik.errors.email}
                            />
                            <Button variant="contained" fullWidth type="submit" disabled={loading}>
                                {loading ? <CircularProgress size={24} /> : 'Send Reset Code'}
                            </Button>
                        </Stack>
                    )}

                    {step === 1 && (
                        <Stack spacing={3} alignItems="center">
                            <Typography variant="body2">
                                Enter code sent to <b>{formik.values.email}</b>
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                {formik.values.otp.map((digit, i) => (
                                    <TextField
                                        key={i}
                                        inputRef={(el) => (inputRefs.current[i] = el)}
                                        value={digit}
                                        variant="outlined"
                                        inputProps={{ maxLength: 1, style: { textAlign: 'center', width: 40 } }}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (/^\d?$/.test(val)) {
                                                const newOtp = [...formik.values.otp];
                                                newOtp[i] = val;
                                                formik.setFieldValue('otp', newOtp);
                                                if (val && i < 3) inputRefs.current[i + 1]?.focus();
                                            }
                                        }}
                                    />
                                ))}
                            </Stack>
                            <Button variant="contained" fullWidth type="submit" disabled={loading}>
                                Verify
                            </Button>
                            <Box textAlign="center">
                                {timer > 0 ? (
                                    <Typography variant="caption">Resend in {timer}s</Typography>
                                ) : (
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="caption"
                                        onClick={handleResend}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        Resend Code
                                    </Link>
                                )}
                                <br />
                                <Link component="button" type="button" variant="caption" color="inherit" onClick={() => setStep(0)}>
                                    Edit Email
                                </Link>
                            </Box>
                        </Stack>
                    )}

                    {step === 2 && (
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                type="password"
                                label="New Password"
                                name="password"
                                onChange={formik.handleChange}
                                value={formik.values.password}
                                error={formik.touched.password && !!formik.errors.password}
                            />

                            {/* Strength Meter */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="caption">Strength</Typography>
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color={`${getStrengthColor(getStrength(formik.values.password))}.main`}
                                    >
                                        {getStrength(formik.values.password)}%
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={getStrength(formik.values.password)}
                                    color={getStrengthColor(getStrength(formik.values.password))}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>

                            <TextField
                                fullWidth
                                type="password"
                                label="Confirm Password"
                                name="confirmPassword"
                                onChange={formik.handleChange}
                                value={formik.values.confirmPassword}
                                error={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                            />
                            <Button variant="contained" fullWidth type="submit" disabled={loading}>
                                Update Password
                            </Button>
                        </Stack>
                    )}

                    {step === 3 && (
                        <Stack spacing={3} textAlign="center">
                            <Typography variant="h5" fontWeight={800}>
                                Success!
                            </Typography>
                            <Typography variant="body2">Your password has been updated.</Typography>
                            <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
                                Login Now
                            </Button>
                        </Stack>
                    )}
                </form>
            </AuthCardWrapper>
        </OnboardingLayout>
    );
};

export default ForgotPasswordPage;
